/**
 * IXVerify v2 — in-app report preview: EVERY page, in place.
 *
 * The report is one PDF — the summary with the verdict and the signatures,
 * then the certified copy: every IXRS assignment page and every kit label
 * photo. Wherever a report is opened (Dashboard, History, the wizard's
 * published screen, the second-check review) this component shows all of it.
 *
 * History: the first version `window.open`ed an attachment presign ("view"
 * meant "download, then go find the file"); the second embedded an inline
 * presign in an `<iframe>`, which on phones drew ONLY THE FIRST PAGE — the
 * IXRS sheet and the kit photos were there but unreachable. Since 2026-10-03
 * the bytes come from `GET /sessions/:id/report` (the api's own origin —
 * pdf.js cannot read a cross-origin bucket URL) and `ReportPages` renders
 * every page with pdf.js, the same on every device.
 *
 * The escape hatches stay: "Open in new tab" and "Download" presign the same
 * object on demand (inline / attachment), exactly as before.
 *
 * R3: the report carries the subject's DOB. The Blob and the short-lived
 * presigned URLs stay inside this component — never logged, never routed,
 * never handed to an error sink.
 */
import { useCallback, useEffect, useState } from 'react';
import { Download, ExternalLink, FileWarning, Loader2 } from 'lucide-react';
import { Modal, primaryBtnCls, secondaryBtnCls } from '../../../ui';
import { getReportBlob, getReportUrl, errorMessage } from '../../lib/api';
import ReportPages from './ReportPages';

export default function ReportPreview({
  sessionId,
  verificationCode = null,
  onClose,
}: {
  sessionId: string;
  /** Shown in the title so a reviewer can tell which dispense they opened. */
  verificationCode?: string | null;
  onClose: () => void;
}) {
  const [file, setFile] = useState<Blob | null>(null);
  const [revision, setRevision] = useState<number | null>(null);
  const [pageCount, setPageCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    setFile(null);
    setPageCount(null);
    try {
      const res = await getReportBlob(sessionId);
      setFile(res.blob);
      setRevision(res.revision);
      setPageCount(res.pageCount);
    } catch (err) {
      setError(errorMessage(err, 'Could not open the report'));
    }
  }, [sessionId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Both escape hatches presign on demand (never stored): inline for a new
  // tab with the browser's own viewer, attachment so Download saves a file.
  const openInNewTab = useCallback(async () => {
    setOpening(true);
    try {
      const res = await getReportUrl(sessionId, { inline: true });
      window.open(res.url, '_blank', 'noopener');
    } catch (err) {
      setError(errorMessage(err, 'Could not open the report'));
    } finally {
      setOpening(false);
    }
  }, [sessionId]);

  const download = useCallback(async () => {
    setDownloading(true);
    try {
      const res = await getReportUrl(sessionId);
      window.open(res.url, '_blank', 'noopener');
    } catch (err) {
      setError(errorMessage(err, 'Could not download the report'));
    } finally {
      setDownloading(false);
    }
  }, [sessionId]);

  const title = verificationCode ? `Report · ${verificationCode}` : 'Verification report';
  const meta = [
    revision != null ? `Revision ${revision}${revision > 1 ? ' — the countersigned copy' : ''}` : null,
    pageCount != null ? `${pageCount} page${pageCount === 1 ? '' : 's'}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Modal isOpen title={title} onClose={onClose} maxWidth="max-w-4xl">
      <div className="space-y-3">
        <div
          className="h-[72vh] min-h-[320px] overflow-y-auto rounded-lg border border-gray-200 bg-gray-100 p-2 sm:p-4 dark:border-gray-700 dark:bg-gray-900"
          data-testid="ixv-report-scroll"
        >
          {error ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
              <FileWarning className="h-8 w-8 text-gray-400 dark:text-gray-500" aria-hidden="true" />
              <p className="text-sm text-gray-600 dark:text-gray-300">{error}</p>
              <button type="button" className={secondaryBtnCls} onClick={() => void load()}>
                Try again
              </button>
            </div>
          ) : file ? (
            <ReportPages file={file} onPageCount={setPageCount} />
          ) : (
            <div className="flex h-full items-center justify-center">
              <Loader2
                className="h-6 w-6 animate-spin text-primary-600 motion-reduce:animate-none dark:text-primary-400"
                aria-hidden="true"
              />
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="ixv-report-meta">{meta || ' '}</p>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className={secondaryBtnCls} disabled={opening} onClick={() => void openInNewTab()}>
              {opening ? (
                <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
              ) : (
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
              )}
              Open in new tab
            </button>
            <button type="button" className={primaryBtnCls} disabled={downloading} onClick={() => void download()}>
              {downloading ? (
                <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
              ) : (
                <Download className="h-4 w-4" aria-hidden="true" />
              )}
              Download
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
